/**
 * Promised-delivery-date helpers, shared by the checkout picker and
 * the cart summary.
 *
 * Both surfaces answer the same customer question ("when does it
 * arrive?") and must answer it identically — and identically to the
 * thank-you page and the admin detail after the order exists. Until
 * KAN-925 this file walked business days from the browser clock while
 * the backend walked them from the order row; the two disagreed by a
 * day (11. 9. vs 10. 9. on razerback, 2026-09-06).
 *
 * The walk now lives in ONE place — the backend's
 * ``app.services.delivery_estimate.promised_delivery_date`` — and the
 * ladder options carry its result as ``estimated_delivery_date``.
 * KAN-1623 — including the carrier's transit days: the caller asks the
 * ladder endpoint with ``transit_days`` and the server walks them in
 * business days. This file used to add them as CALENDAR days, so a
 * Friday hand-over +1 promised a Saturday delivery. It only formats now.
 */

/** ``YYYY-MM-DD`` → local-midnight ``Date`` (no timezone shift). */
export function parseIsoDate(value: string): Date {
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** The backend-computed production finish date of a delivery option. */
export interface DeliveryOptionPromise {
  /** Nullable schválně: ``estimated_delivery_date`` je v API
   *  ``date | None`` a ``promised_date`` má v ``delivery_aggregation``
   *  default ``None``. Frontend to měl jako povinné a na chybějící
   *  hodnotě padal ``Cannot read properties of undefined (reading
   *  'split')`` — tedy rozbitá pokladna, ne chybějící datum. */
  estimated_delivery_date: string | null;
}

/**
 * Promised date for one delivery option, as the backend computed it
 * (production + the transit days the ladder options were asked for).
 */
export function deliveryDateFor(option: DeliveryOptionPromise): Date | null {
  // Bez data ze serveru není co slíbit. Vrací se ``null`` ze stejného
  // důvodu, jaký má v docstringu ``earliestDeliveryDate`` níž: radši
  // mlčet, než si termín vymyslet.
  if (!option.estimated_delivery_date) return null;
  return parseIsoDate(option.estimated_delivery_date);
}

/**
 * Earliest date any of ``options`` could deliver — the "od" in
 * "termín dodání od Y".
 *
 * Returns ``null`` for an empty list so the caller can stay silent
 * rather than invent a date; a tenant with no delivery ladder
 * configured has nothing to promise.
 */
export function earliestDeliveryDate(
  options: DeliveryOptionPromise[],
): Date | null {
  let earliest: Date | null = null;
  for (const option of options) {
    const candidate = deliveryDateFor(option);
    if (candidate === null) continue;
    if (earliest === null || candidate < earliest) {
      earliest = candidate;
    }
  }
  return earliest;
}

/**
 * Render a promised date for the customer. Shared so the cart's
 * "termín dodání od" and the checkout's per-option date are formatted
 * identically as well as computed identically.
 */
export function formatDeliveryDate(
  value: Date | null,
  locale: string,
): string {
  // Prázdný řetězec, ne vymyšlené datum: volající tím pádem vykreslí
  // prázdno místo toho, aby appka spadla.
  if (value === null) return "";
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(value);
}
